import React, { lazy, Suspense, Fragment } from 'react';
import { Routes as RouterRoutes, Route, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';

import { ClimbingBoxLoader } from 'react-spinners';

import MuiTheme from './theme';

// Route Protection
import ProtectedRoute from './components/ProtectedRoute';
import RequireCapability from "./components/RequireCapability";

// Layout Blueprints

import {
  LeftSidebar,
  CollapsedSidebar,
  MinimalLayout,
  PresentationLayout
} from './layout-blueprints';

// Error Pages
import PagesError404 from './components/NotFound';

//Pages Main Application
import UbilSnackbar from "./modules/snackbar/components/snackbar";
import Accounting from "./pages/Accounting";
import Accounts from "./pages/Accounts";
import Batches from "./pages/Batches";
import BillingDashboard from "./pages/BillingDashboard";
import BillingWizard from "./pages/BillingWizard";
import BulkCheckEntry from "./pages/BulkCheckEntry";
import Dashboard from "./pages/Dashboard";
import DevPage from "./pages/Dev";
import Developers from "./pages/Developers";
import Vendors from "./pages/Vendors";
import RateReference from "./pages/RateReference";
import IndividualVendorPage from "./pages/IndividualVendor";
import Disconnect from "./pages/Disconnects";
import ForgotPassword from "./pages/ForgotPassword";
import Home from "./pages/Home";
const AccountManagerPage = lazy(() => import("./pages/IndividualAccount"));
import IndividualMeterPage from "./pages/IndividualMeter";
import IndividualPropertyPage from "./pages/IndividualProperty";
import IndividualPropertyManagerPage from "./pages/IndividualPropertyManager";
import IndividualReport from "./pages/IndividualReport";
import IndividualUnitPage from "./pages/IndividualUnit";
import Login from "./pages/Login";
import MeterReadingsUpload from "./pages/MeterReadingsUpload";
import LockboxUpload from './pages/LockboxUpload';
import Moves from "./pages/Moves";
import MoveInsOuts from "./pages/MoveInsOuts";
import MoveDetail from "./pages/MoveInsOuts/Detail";
import DeletedMoves from "./pages/MoveInsOuts/DeletedMoves";
import ProcessedMoves from "./pages/MoveInsOuts/ProcessedMoves";
import Owners from "./pages/Owners";
import PropertyManagerSearch from "./pages/PMSearch";
import PropertiesPage from "./pages/Properties";
import Reports from "./pages/Reports";
import ResetPassword from "./pages/ResetPassword";
import Settings from "./pages/Settings";
import WorkOrders from "./pages/WorkOrders";
const Complaints = lazy(() => import("./pages/Complaints"));
import PageDownForMaintenance from "./pages/PageDownForMaintenance";
import BatchUnits from "./pages/BatchUnit";
import BillingPdfDownload from "./pages/BillingPdfDownload";
import Users from "./pages/Users"
import ApiKeys from "./pages/ApiKeys";
import PropertyAndGroupsManagement from "./pages/PropertyGroups";
import DisconnectCommandCenter from "./pages/DisconnectCommandCenter";
import ThemeShowcase from "./examples/ThemeShowcase";
import DisconnectSchedules from "./pages/DisconnectSchedules";
import DisconnectDetail from "./pages/DisconnectDetail";
import ContractInvoices from "./pages/ContractInvoices";



import theme from "./theme";

//Custom Components



const Routes = () => {
  const location = useLocation();
  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.99
    },
    in: {
      opacity: 1,
      scale: 1
    },
    out: {
      opacity: 0,
      scale: 1.01
    }
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.4
  };

  const SuspenseLoading = () => {
    return (
      <Fragment>
        <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
          <div className="d-flex align-items-center flex-column px-4">
          </div>
          <div className="text-muted font-size-xl text-center pt-3">
            Please wait while we load data for Ubuil+
            <span className="font-size-lg d-block text-dark">
              Loading...
            </span>
          </div>
        </div>
      </Fragment>
    );
  };




  const mainApplicationRoutes = [
    // Public routes (no authentication required)
    <Route key="home" path="/" element={<Home />} />,
    <Route key="login" path="/login" element={<Login />} />,
    <Route key="forgot-password" path="/forgotpassword" element={<ForgotPassword />} />,
    <Route
      key="reset-password"
      path="/reset-password/:token/:email"
      element={<ResetPassword />}
    />,
    
    // Protected routes (authentication required)
    <Route key="dev" path="/DevPage" element={<ProtectedRoute><DevPage /></ProtectedRoute>} />,
    <Route key="dashboard" path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />,
    <Route key="accounts" path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />,
    <Route key="users" path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />,
    <Route key="accounting" path="/accounting" element={<ProtectedRoute><RequireCapability capability="accountingTools" title="Accounting"><Accounting /></RequireCapability></ProtectedRoute>} />,
    <Route key="page-maintenance" path="/page-maintenance" element={<ProtectedRoute><PageDownForMaintenance /></ProtectedRoute>} />,
    <Route
      key="accountmanager-with-uuid"
      path="/accountmanager/:acctNum/:uuid"
      element={<ProtectedRoute><AccountManagerPage /></ProtectedRoute>}
    />,
    <Route
      key="accountmanager"
      path="/accountmanager/:acctNum"
      element={<ProtectedRoute><AccountManagerPage /></ProtectedRoute>}
    />,
    <Route key="batches" path="/batches/:id" element={<ProtectedRoute><Batches /></ProtectedRoute>} />,
    <Route
      key="billingwizard"
      path="/billingwizard"
      element={<ProtectedRoute><RequireCapability capability="runBilling" title="Billing Wizard"><BillingWizard /></RequireCapability></ProtectedRoute>}
    />,
    <Route
      key="billingwizard-with-uuid"
      path="/billingwizard/:wtUuid"
      element={<ProtectedRoute><RequireCapability capability="runBilling" title="Billing Wizard"><BillingWizard /></RequireCapability></ProtectedRoute>}
    />,
    <Route
      key="property-manager"
      path="/propertyManager/:uuid/:id"
      element={<ProtectedRoute><IndividualPropertyManagerPage /></ProtectedRoute>}
    />,
    <Route
      key="billing-dashboard"
      path="/billingdashboard"
      element={<ProtectedRoute><RequireCapability capability="runBilling" title="Billing Dashboard"><BillingDashboard /></RequireCapability></ProtectedRoute>}
    />,
    <Route
      key="billing-dashboard-with-delay"
      path="/billingdashboard/:querydelay"
      element={<ProtectedRoute><RequireCapability capability="runBilling" title="Billing Dashboard"><BillingDashboard /></RequireCapability></ProtectedRoute>}
    />,
    <Route
      key="property-managers"
      path="/property-managers"
      element={<ProtectedRoute><PropertyManagerSearch /></ProtectedRoute>}
    />,
    <Route key="bulk-check-entry" path="/bulkcheckentry" element={<ProtectedRoute><RequireCapability capability="accountingTools" title="Bulk Check Entry"><BulkCheckEntry /></RequireCapability></ProtectedRoute>} />,
    <Route key="billing-pdf" path="/billingpdf" element={<ProtectedRoute><BillingPdfDownload /></ProtectedRoute>}/>,
    <Route key="developers" path="/developers" element={<ProtectedRoute><Developers /></ProtectedRoute>} />,
    <Route key="vendors" path="/vendors" element={<ProtectedRoute><Vendors /></ProtectedRoute>} />,
    <Route key="rate-reference" path="/rate-reference" element={<ProtectedRoute><RateReference /></ProtectedRoute>} />,
    <Route key="individual-vendor" path="/vendor/:uuid" element={<ProtectedRoute><IndividualVendorPage /></ProtectedRoute>} />,
    <Route key="disconnect" path="/disconnect" element={<ProtectedRoute><RequireCapability capability="disconnects" title="Disconnects"><Disconnect /></RequireCapability></ProtectedRoute>} />,
    <Route key="disconnect-management" path="/disconnect-management" element={<ProtectedRoute><RequireCapability capability="disconnects" title="Disconnect Management"><PropertyAndGroupsManagement /></RequireCapability></ProtectedRoute>} />,
    <Route key="disconnect-command-center" path="/disconnect-command-center" element={<ProtectedRoute><RequireCapability capability="disconnects" title="Disconnect Command Center"><DisconnectCommandCenter /></RequireCapability></ProtectedRoute>} />,
    <Route key="disconnect-schedules" path="/disconnect-schedules" element={<ProtectedRoute><RequireCapability capability="disconnects" title="Disconnect Schedules"><DisconnectSchedules /></RequireCapability></ProtectedRoute>} />,
    <Route key="disconnect-detail" path="/disconnect/:uuid" element={<ProtectedRoute><DisconnectDetail /></ProtectedRoute>} />,
    <Route key="contract-invoices" path="/contract-invoices" element={<ProtectedRoute><RequireCapability capability="disconnects" title="Contract Invoices"><ContractInvoices /></RequireCapability></ProtectedRoute>} />,
    <Route key="moves" path="/moves" element={<ProtectedRoute><RequireCapability capability="moves" title="Moves"><Moves /></RequireCapability></ProtectedRoute>} />,
    <Route key="move-detail" path="/move-ins-outs/:kind/:uuid" element={<ProtectedRoute><RequireCapability capability="moves" title="Moves Dashboard"><MoveDetail /></RequireCapability></ProtectedRoute>} />,
    <Route key="moves-deleted" path="/move-ins-outs/deleted" element={<ProtectedRoute><RequireCapability capability="moves" title="Moves Dashboard"><DeletedMoves /></RequireCapability></ProtectedRoute>} />,
    <Route key="moves-processed" path="/move-ins-outs/processed" element={<ProtectedRoute><RequireCapability capability="moves" title="Moves Dashboard"><ProcessedMoves /></RequireCapability></ProtectedRoute>} />,
    <Route key="move-ins-outs" path="/move-ins-outs" element={<ProtectedRoute><RequireCapability capability="moves" title="Moves Dashboard"><MoveInsOuts /></RequireCapability></ProtectedRoute>} />,
    <Route key="work-orders" path="/workorders" element={<ProtectedRoute><WorkOrders /></ProtectedRoute>} />,
    <Route key="complaints" path="/complaints" element={<ProtectedRoute><Complaints /></ProtectedRoute>} />,
    <Route key="owners" path="/owners" element={<ProtectedRoute><Owners /></ProtectedRoute>} />,
    <Route key="properties" path="/properties" element={<ProtectedRoute><PropertiesPage /></ProtectedRoute>} />,
    <Route
      key="individual-property"
      path="/property/:uuid/:id/:propertyManagerId"
      element={<ProtectedRoute><IndividualPropertyPage /></ProtectedRoute>}
    />,
    <Route key="batch-units" path="/batch-units/property/:propertyUuid" element={<ProtectedRoute><RequireCapability capability="editRecords" title="Add Units"><BatchUnits /></RequireCapability></ProtectedRoute>} />,
    <Route
      key="individual-unit"
      path="/unit/:uuid/:id"
      element={<ProtectedRoute><IndividualUnitPage /></ProtectedRoute>}
    />,
    <Route key="individual-meter" path="/meter/:uuid" element={<ProtectedRoute><IndividualMeterPage /></ProtectedRoute>} />,
    <Route
      key="meter-readings-upload"
      path="/meter-readings-upload"
      element={<ProtectedRoute><RequireCapability capability="meterReadings" title="Meter Readings Upload"><MeterReadingsUpload /></RequireCapability></ProtectedRoute>}
    />,
    <Route
      key="lockbox-upload"
      path="/lockbox-upload"
      element={<ProtectedRoute><RequireCapability capability="accountingTools" title="Lockbox Upload"><LockboxUpload /></RequireCapability></ProtectedRoute>}
    />,
    <Route key="reports" path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />,
    <Route
      key="individual-report"
      path="/reports/:reportName"
      element={<ProtectedRoute><IndividualReport /></ProtectedRoute>}
    />,
    <Route key="settings" path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />,
    <Route
      key="api-keys"
      path="/api-keys"
      element={<ProtectedRoute><ApiKeys /></ProtectedRoute>}
    />,
    <Route
      key="theme-showcase"
      path="/theme-showcase"
      element={<ProtectedRoute><ThemeShowcase /></ProtectedRoute>}
    />,
  ]


  return (
    <ThemeProvider theme={MuiTheme}>
      <StylesThemeProvider theme={MuiTheme}>
        <UbilSnackbar />
        <AnimatePresence>
          <Suspense fallback={<SuspenseLoading />}>
            <RouterRoutes>
              {mainApplicationRoutes}
              <Route path="/404" element={<PagesError404 />} />
            </RouterRoutes>
          </Suspense>
        </AnimatePresence>
      </StylesThemeProvider>
    </ThemeProvider>
  );
};

export default Routes;
